import type {ComponentProps} from 'react';

import React from 'react';

import 'overlayscrollbars/overlayscrollbars.css';
import {OverlayScrollbars, ClickScrollPlugin} from 'overlayscrollbars';
import {OverlayScrollbarsComponent} from 'overlayscrollbars-react';
import bowser from 'bowser';
import {withCssModule} from '@pb/kit-util-with-css-module';

import {getDefaultOptions} from './utils/get-default-options';
import styles from './scroll.component.module.styl';

import type {OverlayScrollbarsComponentProps} from 'overlayscrollbars-react';

// Note: If scrollbars.clickScroll set to true the ClickScrollPlugin is required.
OverlayScrollbars.plugin(ClickScrollPlugin);

const classNames = withCssModule(styles);

// TODO: Temporary Safari fix - remove when Safari fixes container-query units bug in animations
// Related to: https://bugs.webkit.org/show_bug.cgi?id=299952, https://github.com/WebKit/WebKit/pull/52296
const isSafari = bowser.safari;

type NativeProps = Omit<OverlayScrollbarsComponentProps, 'ref' | 'onScroll'>;

export interface ScrollProps extends NativeProps {
    /**
     * When `true`, applies the dark-theme scrollbar styles. Use inside
     * dark-background surfaces such as the sidebar navigation to keep the
     * scrollbar visible against dark colors.
     */
    darkMode?: boolean,
    /**
     * Standard React `onScroll` handler forwarded to the inner scroll
     * container. Receives a `UIEvent<HTMLDivElement>` identical to the native
     * `<div onScroll={…}>` callback.
     */
    onScroll?: ComponentProps<'div'>['onScroll'],
}

/**
 * Custom scrollbar container powered by [OverlayScrollbars](https://kingsora.github.io/OverlayScrollbars/).
 *
 * Renders a styled scrollable region that replaces the native browser scrollbar
 * with a design-system consistent overlay scrollbar. Supports `darkMode` for
 * dark-background contexts (e.g. the sidebar navigation). The `onScroll` prop
 * is forwarded as a standard React `UIEvent` handler.
 *
 * All other `OverlayScrollbarsComponent` props (including `options` and `events`)
 * are spread through, allowing full control over scrollbar behavior such as
 * `clickScroll`, `overflow`, and `scrollbars.autoHide`.
 *
 * @see [OverlayScrollbars docs](https://kingsora.github.io/OverlayScrollbars/)
 */
export function Scroll(props: ScrollProps) {
    const {
        darkMode,
        options,
        onScroll,
        events,
        className,
        ...otherProps
    } = props;

    return (
        <OverlayScrollbarsComponent
            {...otherProps}
            className={classNames(className, {'scroll-safari-fix': isSafari})}
            events={{
                ...events,
                scroll: (_, event) => {
                    onScroll?.(event as unknown as React.UIEvent<HTMLDivElement>);
                    if (typeof events === 'object' && typeof events?.scroll === 'function') {
                        events.scroll(_, event);
                    }
                },
            }}
            defer={true}
            options={getDefaultOptions({
                darkMode,
                ...options,
            })}
        />
    );
}

